import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearInventariosINV = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Mutación para guardar el nuevo inventario
  const {
    mutateAsync: SaveCreacionInventario,
    isPending: isSaving,
    isError: isErrorSaving,
  } = useMutation({
    queryKey: ["isCreatingInventarioINV"],
    fn: async (data) => {
      const response = await api.post("/InventariosINV/createInventariosINV", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  // Estado inicial según estructura de tabla inbinv
  const [formData, setFormData] = useState({
    invcodigo: "",
    invdescri: "",
    invstatus: "A",
  })

  // Manejar cambios con normalización (Upper Case y recortes)
  const handleInputChange = (field, value) => {
    let finalValue = value.toUpperCase()

    if (field === "invcodigo") finalValue = finalValue.slice(0, 2)
    if (field === "invdescri") finalValue = finalValue.slice(0, 30)

    setFormData((prev) => ({
      ...prev,
      [field]: finalValue,
    }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Validaciones básicas del lado del cliente[cite: 17]
    if (!formData.invcodigo.trim()) return showWarning("El Código de Inventario es obligatorio")
    if (!formData.invdescri.trim()) return showWarning("La Descripción de Inventario es obligatoria")

    try {
      await SaveCreacionInventario(formData)
    } catch (error) {
      console.error("Error al crear el inventario:", error)
    }
  }

  // Buscar acción de guardado en la barra de acciones (CREAR, GUARDAR o EJECUTAR)
  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GUARDAR" || action?.acccaption === "CREAR" || action?.acccaption === "EJECUTAR",
  )

  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nuevo Inventario</b>
        </div>

        <CustomBackdrop isLoading={isSaving} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3 }}>
              Datos del Inventario
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Código (2 Caracteres) *"
                  value={formData.invcodigo}
                  onChange={(e) => handleInputChange("invcodigo", e.target.value)}
                  error={isErrorSaving && !formData.invcodigo}
                  inputProps={{ maxLength: 2 }}
                  onFocus={(e) => e.target.select()}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Descripción de Inventario *"
                  value={formData.invdescri}
                  onChange={(e) => handleInputChange("invdescri", e.target.value)}
                  error={isErrorSaving && !formData.invdescri}
                  inputProps={{ maxLength: 30 }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={formData.invstatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, invstatus: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearInventariosINV
